import React, { FC } from 'react'
import { ContentfulRichText } from '@/atoms/ContentfulRichText'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { Image } from '@/atoms/Image'
import { EyebrowSM, ParagraphLG, TitleLG } from '@/atoms/Text'
import { formatPrice } from '@/services/PriceFormatter'
import { GiveawayPrizeItem, GiveawayPrizesTab, Maybe } from '@/types/codegen-contentful'
import { useTranslate } from '@/utils/translate/translate-client'
import { giveawayRichTextOverrides } from '../../utils/richtextUtils'

interface Props {
  content: GiveawayPrizesTab
}

const renderPrizeSection = (prizes: Maybe<GiveawayPrizeItem>[]) => {
  return prizes.map((prizeItem) => {
    if (!prizeItem) return null
    return <PrizeItem key={prizeItem.sys.id} {...prizeItem} />
  })
}

export const PrizeItemsTab: FC<Props> = ({ content }) => {
  return (
    <div className="mx-4 mb-20 mt-10 flex flex-col gap-14 md:ml-11 md:mr-8 md:mt-20">
      <ContentfulRichText {...content.text} renderOptionOverrides={giveawayRichTextOverrides} />
      {content.prizesSectionsCollection?.items?.map((section) => (
        <div key={section?.sys?.id}>
          {section?.title && (
            <TitleLG
              color="giveaway-text-primary-dark"
              className="md:photon-heading-sm mb-3 font-semibold md:font-bold"
            >
              {section.title}
            </TitleLG>
          )}
          {!!section?.prizesCollection?.items?.length && (
            <div className="grid gap-14 lg:grid-cols-2">{renderPrizeSection(section.prizesCollection.items)}</div>
          )}
        </div>
      ))}
    </div>
  )
}

const PrizeItem = ({
  imageUrl,
  altText,
  title,
  value,
  url,
  sponsorName,
  representativePrizeShown,
}: GiveawayPrizeItem) => {
  const { t } = useTranslate('tickets')

  const imageContent = (
    <div className="aspect-h-9 aspect-w-16 relative overflow-hidden rounded-lg">
      <Image
        src={imageUrl ?? ''}
        alt={altText ?? ''}
        fill
        className="object-cover transition-transform duration-300 "
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      />

      {representativePrizeShown && (
        <div className="absolute inset-0 ">
          <div className="relative h-full w-full">
            <Image
              src="/v1747076634/repprizebig_ty4oa1"
              alt="Representitive Prize Shown"
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          </div>
        </div>
      )}
      {url && sponsorName && (
        <div className="absolute inset-0 flex items-end justify-end p-[10px]">
          <div className="inline-flex items-center gap-1 rounded-none bg-white/90 px-2 py-1 text-xs font-medium text-giveaway-primary shadow-sm backdrop-blur-sm transition-all duration-200 group-hover:bg-white group-hover:shadow-md">
            <span>{t('visitSponsorName', 'Visit {{sponsorName}}', { sponsorName })}</span>
            <ExternalLinkIcon size={12} className="relative top-px" />
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="group flex flex-col pb-4">
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="relative overflow-hidden rounded-lg">
          {imageContent}
        </a>
      ) : (
        imageContent
      )}
      <div className="flex justify-between gap-4 pb-1 pt-2">
        <div className="w-2/3">
          <EyebrowSM weight="semibold" color="giveaway-text-subtle-light">
            {t('item', 'item')}
          </EyebrowSM>
          <ParagraphLG weight="normal" color="giveaway-text-primary-dark">
            {title}
          </ParagraphLG>
        </div>
        {value && (
          <div>
            <EyebrowSM weight="semibold" color="giveaway-subtle">
              {t('value', 'value')}
            </EyebrowSM>
            <ParagraphLG weight="normal" color="giveaway-text-primary-dark">
              {formatPrice(value, { currency: 'USD', includeDecimals: false })}
            </ParagraphLG>
          </div>
        )}
      </div>
    </div>
  )
}
